import { useRef } from 'react';
import { closeSnackbar, SnackbarProvider as NotistackProvider } from 'notistack';

import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import GlobalStyles from '@mui/material/GlobalStyles';

import Iconify from '../iconify';
import { useSettingsContext } from '../settings';
import { StyledIcon, StyledNotistack } from './styles';
import { SnackbarProvider as ErrorSnackbarProvider } from './provider';

/**
 * notistack SnackbarContainer 기본 z-index는 1400이다.
 * 인스턴트 러닝 모바일 댓글 전체화면 Dialog는 theme.zIndex.modal + 150(≈1450)을 쓰므로,
 * 그대로 두면 업로드 실패 스낵바가 Dialog 뒤에 그려져 사용자에게는 “미노출”처럼 보인다.
 * modal + 250으로 올려 댓글 Dialog·일반 모달 위에 두되, HEIC 전면 오버레이(100000)보다는 낮게 유지한다.
 */
const NOTISTACK_ABOVE_MODAL_DIALOG_Z_INDEX_OFFSET = 250;

type Props = {
  children: React.ReactNode;
};

export default function SnackbarProvider({ children }: Readonly<Props>) {
  const settings = useSettingsContext();
  const theme = useTheme();
  const isRTL = settings.themeDirection === 'rtl';
  const notistackRef = useRef<any>(null);
  const notistackContainerZIndex = theme.zIndex.modal + NOTISTACK_ABOVE_MODAL_DIALOG_Z_INDEX_OFFSET;

  return (
    <>
      <GlobalStyles
        styles={{
          '.notistack-SnackbarContainer': {
            zIndex: `${notistackContainerZIndex} !important`,
          },
        }}
      />
      <NotistackProvider
        ref={notistackRef}
        maxSnack={5}
        preventDuplicate
        autoHideDuration={1000}
        TransitionComponent={isRTL ? Collapse : undefined}
        variant="success"
        iconVariant={{
          info: (
            <StyledIcon color="info">
              <Iconify icon="eva:info-fill" width={24} />
            </StyledIcon>
          ),
          success: (
            <StyledIcon color="success">
              <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
            </StyledIcon>
          ),
          warning: (
            <StyledIcon color="warning">
              <Iconify icon="eva:alert-triangle-fill" width={24} />
            </StyledIcon>
          ),
          error: (
            <StyledIcon color="error">
              <Iconify icon="solar:danger-bold" width={24} />
            </StyledIcon>
          ),
        }}
        Components={{
          default: StyledNotistack,
          info: StyledNotistack,
          success: StyledNotistack,
          warning: StyledNotistack,
          error: StyledNotistack,
        }}
        action={(snackbarId) => renderSnackbarAction(snackbarId)}
      >
        <ErrorSnackbarProvider>{children}</ErrorSnackbarProvider>
      </NotistackProvider>
    </>
  );
}

const renderSnackbarAction = (snackbarId: string | number) => (
  <IconButton size="small" onClick={() => closeSnackbar(snackbarId)} sx={{ p: 0.5 }}>
    <Iconify width={16} icon="mingcute:close-line" />
  </IconButton>
);
